'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ArrowUp, ArrowUpRight } from 'lucide-react';
import { useGeoData } from '@/hooks/useGeoData';
import { useStreamedResponse } from '@/hooks/useStreamedResponse';
import MessageList from '@/components/chat-bot/messageList';
import { CONTENT_KIND, PlotFeature, ZoneFeature } from '@/lib/types';
import InitialHero from '@/components/chat-bot/InitialHero';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import MapView from '@/components/chat-bot/mapView';
import { reprojectFeatureCollectionToWgs84 } from '@/lib/reproject';
import { OFFICIAL_SOURCES } from '@/lib/constants';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@opaz/ui-components';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from '@tanstack/react-router';

delete (L.Icon.Default as unknown as { prototype: { _getIconUrl: unknown } }).prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const baseUrl = import.meta.env.VITE_STRAPI_URL;

export default function ChatWithMapView() {
  const [sessionId, setSessionId] = useState(() => uuidv4());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { t, i18n } = useTranslation();
  const lastLanguageRef = useRef(i18n.language);
  const analytics = useAnalytics();
  const isMobile = useIsMobile();
  const location = useLocation();
  const hasTrackedEngagement = useRef(false);

  const { zonesData } = useGeoData();
  const {
    messages,
    sendMessage,
    isLoading,
    postprocessing,
    followupQuestions,
    setFollowupQuestions,
    resetConversation,
  } = useStreamedResponse(`${baseUrl}/agno-agent/query/stream`, sessionId);

  const [input, setInput] = useState('');

  // Cache of loaded plot features per zone code
  const [zonePlotsByCode, setZonePlotsByCode] = useState<Record<string, PlotFeature[]>>({});
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Track chatbot engagement on first mount
  useEffect(() => {
    if (analytics && !hasTrackedEngagement.current) {
      const entryPoint = location.search?.entryPoint || 'direct_navigation';
      analytics.trackChatbotEngaged(entryPoint, isMobile ? 'mobile' : 'desktop');
      hasTrackedEngagement.current = true;
    }
  }, [analytics, isMobile, location.search]);

  useEffect(() => {
    if (!i18n.language) return;
    if (lastLanguageRef.current === i18n.language) return;

    lastLanguageRef.current = i18n.language;
    setSessionId(uuidv4());
    resetConversation();
    setInput('');
  }, [i18n.language, resetConversation]);

  // Load plots GeoJSON only if exactly one zone code is detected (and plots were requested)
  useEffect(() => {
    const zoneCodes = postprocessing.zoneCodes?.map((c) => String(c).trim().toUpperCase()) ?? [];
    if (!postprocessing.plotDetected) return;
    const uniqueCodes = Array.from(new Set(zoneCodes));
    if (uniqueCodes.length !== 1) return;

    const code = uniqueCodes[0];
    if (!code || zonePlotsByCode[code]) return;
    const url = `/data/${code}.geojson`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((fc) => {
        const reprojected = reprojectFeatureCollectionToWgs84(fc);
        const features = (reprojected?.features ?? []) as PlotFeature[];
        console.log(`[plots] loaded ${features.length} features from ${url}`);
        setZonePlotsByCode((prev) => ({ ...prev, [code]: features }));
      })
      .catch((err) => {
        console.error(`Failed to load plots for zone ${code} from ${url}`, err);
        setZonePlotsByCode((prev) => ({ ...prev, [code]: [] }));
      });
  }, [postprocessing.zoneCodes, postprocessing.plotDetected, zonePlotsByCode]);

  const { matchedZones, matchedPlots } = useMemo(() => {
    const zoneCodes = postprocessing.zoneCodes?.map(String) ?? [];

    const allZoneFeatures = (zonesData?.features ?? []) as ZoneFeature[];
    const zones = allZoneFeatures.filter((f) => {
      const code = String(f?.properties?.code ?? '');
      return code && zoneCodes.includes(code);
    });

    const uniqueCodes = Array.from(new Set(zoneCodes.map((c) => String(c).trim().toUpperCase())));
    const plots =
      postprocessing.plotDetected && uniqueCodes.length === 1 ? (zonePlotsByCode[uniqueCodes[0]] ?? []) : [];

    return {
      matchedZones: zones,
      matchedPlots: plots,
    };
  }, [postprocessing.zoneCodes, postprocessing.plotDetected, zonesData?.features, zonePlotsByCode]);

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    const el = scrollRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });

    setInput('');
    await sendMessage(trimmed);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, followupQuestions, isLoading]);

  const quickAsk = async (text: string) => {
    setInput('');
    setFollowupQuestions([]);
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });

    await sendMessage(text);
  };

  return (
    <div className="mx-auto max-w-7xl px-2 sm:px-3 md:px-4 lg:px-6 min-h-[calc(100svh-9rem)] sm:min-h-[calc(100svh-10rem)] flex flex-col lg:flex-row gap-3 sm:gap-4 mt-4 sm:mt-6 lg:mt-8 mb-4 sm:mb-8 lg:mb-12 lg:h-[calc(100svh-10rem)] lg:overflow-hidden">
      {/* Chat column */}
      <motion.div
        layout
        transition={{ duration: 0.45, ease: [0.25, 0.8, 0.25, 1] }}
        className={cn(
          'flex-1 min-w-0',
          'flex flex-col lg:h-full bg-white/80 backdrop-blur', // no border, h-full only on desktop
          'rounded-lg lg:rounded-2xl', // rounded on mobile too
          'min-h-[50svh] lg:min-h-0', // minimum height on mobile to allow map visibility
        )}
      >
        {!messages.length && <InitialHero onAsk={quickAsk} />}

        <div
          ref={scrollRef}
          className={cn(
            'flex-1 min-h-0',
            'overflow-y-auto px-2 sm:px-3 md:px-4 pe-1 scroll-smooth space-y-3 sm:space-y-4',
            'chat-scroll',
          )}
        >
          <MessageList
            messages={messages.filter(
              (m) => m.type === 'user' || m.kind === CONTENT_KIND.Text || m.kind === CONTENT_KIND.Error,
            )}
          />
        </div>

        {!!followupQuestions.length && (
          <div className="mt-2 sm:mt-3 px-2 sm:px-3 md:px-4">
            <AnimatePresence mode="popLayout">
              <div className={cn('flex flex-col gap-2', 'md:flex-row md:flex-wrap')}>
                {followupQuestions.map((q, i) => (
                  <motion.button
                    key={q}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ delay: i * 0.02, duration: 0.15 }}
                    onClick={() => quickAsk(q)}
                    className="w-full md:w-auto md:flex-shrink-0 group flex items-center gap-1 rounded-xl bg-[#EBF3F7] cursor-pointer transition-colors px-3 py-2 shadow-sm hover:bg-[#dbe9f0]"
                  >
                    <p className="text-[#0F172A] text-sm leading-snug flex-1 text-left md:text-center">{q}</p>
                    <ArrowUpRight
                      className="w-4 h-4 text-[#005287] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0"
                      aria-hidden="true"
                    />
                  </motion.button>
                ))}
              </div>
            </AnimatePresence>
          </div>
        )}

        <div className="sticky bottom-0 mt-2 sm:mt-3 px-2 sm:px-3 md:px-4 lg:px-6 pb-2 sm:pb-3 bg-white/80 backdrop-blur lg:bg-transparent lg:backdrop-blur-none">
          <div className="flex items-center justify-between rounded-full border px-2 sm:px-3 md:px-4 py-2 bg-white shadow-sm">
            <div className="flex items-center gap-2 w-full min-w-0">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && handleSubmit()}
                placeholder={t('chatBot.input.placeholder')}
                className="w-full border-none bg-transparent text-xs sm:text-sm focus:outline-none disabled:opacity-60"
                disabled={isLoading}
              />
            </div>

            <button
              onClick={handleSubmit}
              className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 bg-[#005287] rounded-full disabled:opacity-60 ml-2"
              disabled={!input.trim() || isLoading}
              aria-busy={isLoading}
            >
              <ArrowUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </button>
          </div>
          {/* Disclaimer */}
          <div className="mt-2 sm:mt-3">
            <p className="text-[10px] sm:text-xs leading-snug text-gray-400">
              Disclaimer: The information provided by this chatbot may be incomplete, outdated, or inaccurate — please
              verify details from
              <button
                type="button"
                onClick={() => setSourcesOpen(true)}
                className="ml-1 underline text-[#005287] hover:text-[#003e66]"
              >
                official sources
              </button>
              .
            </p>
          </div>
          <div className="h-[env(safe-area-inset-bottom)]" />
        </div>
      </motion.div>

      {/* Map column */}
      <div
        className={cn(
          'w-full lg:w-[46%]',
          'flex-none',
          'min-h-[35svh] sm:min-h-[40svh] lg:min-h-0',
          'h-[35svh] sm:h-[40svh] lg:h-full',
          'border rounded-lg overflow-hidden',
          'relative',
        )}
      >
        <MapView zones={matchedZones} plots={matchedPlots} />
      </div>
      {/* Official sources dialog */}
      <Dialog open={sourcesOpen} onOpenChange={setSourcesOpen}>
        <DialogContent className="max-w-xl z-[1000]">
          <DialogHeader>
            <DialogTitle>Official Sources</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {OFFICIAL_SOURCES.map((s) => (
              <div key={s.url} className="group rounded-lg border border-gray-200 px-4 py-2 bg-white/80 shadow-sm">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#00446d] hover:text-[#0068a8] underline-offset-2 hover:underline transition-colors duration-150"
                >
                  {s.name}
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-80 group-hover:opacity-100 transition-opacity duration-150" />
                </a>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSourcesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
